import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../AuthContext';

const API = '/api';

const DEFAULT_SUPPLIERS = ['MEDIARAM', 'aKSessories', 'NETONE', 'IT4YOU', 'SAMSUNG MICROTECH ΔΑΚΑΣ'];

export default function Inventory() {
  const { isAdmin } = useAuth();
  const [parts,     setParts]     = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [search,    setSearch]    = useState('');
  const [msg,       setMsg]       = useState(null);
  const [editing,   setEditing]   = useState(null);
  const [suppliers, setSuppliers] = useState(() => {
    const saved = localStorage.getItem('mg_suppliers');
    return saved ? JSON.parse(saved) : DEFAULT_SUPPLIERS;
  });
  const [newSupplier, setNewSupplier] = useState('');
  const [showSupplierModal, setShowSupplierModal] = useState(false);

  const [form, setForm] = useState({
    code: '', name: '', price_no_vat: '', price_with_vat: '', quantity: '', supplier: ''
  });

  const fetchParts = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/parts`, { params: search ? { search } : {} });
      setParts(data);
    } catch { showMsg('error', 'Σφάλμα φόρτωσης δεδομένων'); }
    finally { setLoading(false); }
  }, [search]);

  useEffect(() => { fetchParts(); }, [fetchParts]);

  function showMsg(type, text) { setMsg({ type, text }); setTimeout(() => setMsg(null), 3500); }
  function setField(k, v) { setForm(f => ({ ...f, [k]: v })); }

  function handleNoVat(v) {
    setField('price_no_vat', v);
    if (v !== '' && !isNaN(v)) setField('price_with_vat', (parseFloat(v) * 1.24).toFixed(2));
  }
  function handleWithVat(v) {
    setField('price_with_vat', v);
    if (v !== '' && !isNaN(v)) setField('price_no_vat', (parseFloat(v) / 1.24).toFixed(2));
  }

  function resetForm() {
    setForm({ code: '', name: '', price_no_vat: '', price_with_vat: '', quantity: '', supplier: '' });
    setEditing(null);
  }

  function startEdit(part) {
    setEditing(part);
    setForm({ code: part.code, name: part.name, price_no_vat: part.price_no_vat, price_with_vat: part.price_with_vat, quantity: part.quantity ?? 0, supplier: part.supplier || '' });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.code.trim() || !form.name.trim()) { showMsg('error', 'Κωδικός και Ονομασία είναι υποχρεωτικά'); return; }
    try {
      const payload = { code: form.code.trim(), name: form.name.trim(), price_no_vat: parseFloat(form.price_no_vat)||0, price_with_vat: parseFloat(form.price_with_vat)||0, quantity: parseInt(form.quantity)||0, supplier: form.supplier || null };
      if (editing) {
        await axios.put(`${API}/parts/${editing.id}`, payload);
        showMsg('success', 'Το ανταλλακτικό ενημερώθηκε');
      } else {
        await axios.post(`${API}/parts`, payload);
        showMsg('success', 'Το ανταλλακτικό αποθηκεύτηκε');
      }
      resetForm(); fetchParts();
    } catch (err) { showMsg('error', err.response?.data?.error || 'Σφάλμα αποθήκευσης'); }
  }

  async function handleDelete(id, code) {
    if (!window.confirm(`Διαγραφή ανταλλακτικού "${code}";`)) return;
    try { await axios.delete(`${API}/parts/${id}`); showMsg('success', 'Διαγράφηκε'); fetchParts(); }
    catch { showMsg('error', 'Σφάλμα διαγραφής'); }
  }

  async function incrementQty(id) {
    try { await axios.patch(`${API}/parts/${id}/quantity`, { amount: 1 }); fetchParts(); }
    catch { showMsg('error', 'Σφάλμα ενημέρωσης ποσότητας'); }
  }

  function addSupplier() {
    if (!newSupplier.trim()) return;
    const updated = [...suppliers, newSupplier.trim().toUpperCase()];
    setSuppliers(updated);
    localStorage.setItem('mg_suppliers', JSON.stringify(updated));
    setNewSupplier('');
  }

  function removeSupplier(s) {
    const updated = suppliers.filter(x => x !== s);
    setSuppliers(updated);
    localStorage.setItem('mg_suppliers', JSON.stringify(updated));
  }

  return (
    <div className="fade-in">
      <div className="page-header">
        <div>
          <div className="page-title">Αποθήκη Ανταλλακτικών</div>
          <div className="page-subtitle">{parts.length} ανταλλακτικά καταχωρημένα</div>
        </div>
        {/* Ορατό σε όλους */}
        <button className="btn-ghost" onClick={() => setShowSupplierModal(true)}>Διαχείριση Προμηθευτών</button>
      </div>

      {msg && <div className={`alert alert-${msg.type}`}>{msg.text}</div>}

      <div className="card" style={{ marginBottom: 24 }}>
        <div className="card-title">{editing ? 'Επεξεργασία Ανταλλακτικού' : 'Νέο Ανταλλακτικό'}</div>
        <form onSubmit={handleSubmit}>
          <div className="form-grid form-grid-2" style={{ marginBottom: 14 }}>
            <div className="field field-required">
              <label>Κωδικός Ανταλλακτικού</label>
              <input value={form.code} onChange={e => setField('code', e.target.value)} placeholder="π.χ. SCR-IPH14-001" className="mono" />
            </div>
            <div className="field field-required">
              <label>Ονομασία Ανταλλακτικού</label>
              <input value={form.name} onChange={e => setField('name', e.target.value)} placeholder="π.χ. Οθόνη iPhone 14 Original" />
            </div>
          </div>
          <div className="form-grid form-grid-2" style={{ marginBottom: 18 }}>
            <div className="field">
              <label>Τιμή</label>
              <div className="price-group">
                <div>
                  <div className="price-label">Χωρίς ΦΠΑ (€)</div>
                  <input type="number" min="0" step="0.01" value={form.price_no_vat} onChange={e => handleNoVat(e.target.value)} placeholder="0.00" />
                </div>
                <div>
                  <div className="price-label">Με ΦΠΑ 24% (€)</div>
                  <input type="number" min="0" step="0.01" value={form.price_with_vat} onChange={e => handleWithVat(e.target.value)} placeholder="0.00" />
                </div>
              </div>
            </div>
            <div className="form-grid form-grid-2">
              <div className="field">
                <label>Ποσότητα</label>
                <input type="number" min="0" value={form.quantity} onChange={e => setField('quantity', e.target.value)} placeholder="0" />
              </div>
              <div className="field">
                <label>Προμηθευτής</label>
                <select value={form.supplier} onChange={e => setField('supplier', e.target.value)}>
                  <option value="">— Επιλέξτε —</option>
                  {suppliers.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button type="submit" className="btn-primary">{editing ? 'Ενημέρωση' : 'Καταχώρηση'}</button>
            {editing && <button type="button" className="btn-ghost" onClick={resetForm}>Ακύρωση</button>}
          </div>
        </form>
      </div>

      <div className="card">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18 }}>
          <div className="card-title" style={{ marginBottom: 0 }}>Καταχωρημένα Ανταλλακτικά</div>
          <div className="search-bar">
            <span className="search-icon"><SearchIcon /></span>
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Αναζήτηση κωδικού / ονομασίας..." />
          </div>
        </div>

        {loading ? (
          <div className="empty-state"><div className="empty-state-text">Φόρτωση...</div></div>
        ) : parts.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">📦</div>
            <div className="empty-state-text">{search ? 'Δεν βρέθηκαν αποτελέσματα' : 'Δεν υπάρχουν ανταλλακτικά ακόμα'}</div>
          </div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Κωδικός</th>
                  <th>Ονομασία</th>
                  <th>Χωρίς ΦΠΑ</th>
                  <th>Με ΦΠΑ</th>
                  <th>Ποσότητα</th>
                  <th>Προμηθευτής</th>
                  <th>Καταχώρηση</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {parts.map(p => (
                  <tr key={p.id}>
                    <td><span className="mono" style={{ color: 'var(--accent)', fontSize: 12 }}>{p.code}</span></td>
                    <td>{p.name}</td>
                    <td><span className="mono">{formatEur(p.price_no_vat)}</span></td>
                    <td><span className="mono" style={{ color: 'var(--green)' }}>{formatEur(p.price_with_vat)}</span></td>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ fontWeight: 700, fontSize: 15, color: p.quantity === 0 ? 'var(--danger)' : p.quantity <= 2 ? '#f59e0b' : 'var(--green)' }}>
                          {p.quantity ?? 0}
                        </span>
                        <button className="btn-ghost btn-sm" onClick={() => incrementQty(p.id)}
                          style={{ padding: '2px 10px', fontSize: 18, fontWeight: 700, lineHeight: 1 }}>+</button>
                      </div>
                    </td>
                    <td style={{ fontSize: 12, color: 'var(--text-muted)' }}>{p.supplier || '—'}</td>
                    <td style={{ color: 'var(--text-muted)', fontSize: 12 }}>{formatDate(p.created_at)}</td>
                    <td>
                      <div style={{ display: 'flex', gap: 4 }}>
                        <button className="btn-ghost btn-sm" onClick={() => startEdit(p)}>Επεξεργασία</button>
                        {isAdmin && <button className="btn-danger btn-sm" onClick={() => handleDelete(p.id, p.code)}>Διαγραφή</button>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Supplier Modal */}
      {showSupplierModal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowSupplierModal(false)}>
          <div className="modal" style={{ maxWidth: 420 }}>
            <div className="modal-header">
              <div className="modal-title">Διαχείριση Προμηθευτών</div>
              <button className="close-btn" onClick={() => setShowSupplierModal(false)}><CloseIcon /></button>
            </div>
            <div className="modal-body">
              <div style={{ marginBottom: 16 }}>
                {suppliers.map(s => (
                  <div key={s} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', background: 'var(--surface)', borderRadius: 6, marginBottom: 6 }}>
                    <span style={{ fontWeight: 600, fontSize: 13 }}>{s}</span>
                    {/* Διαγραφή μόνο admin */}
                    {isAdmin && (
                      <button className="btn-danger btn-sm" onClick={() => removeSupplier(s)}>Διαγραφή</button>
                    )}
                  </div>
                ))}
              </div>
              {/* Προσθήκη — όλοι */}
              <div style={{ display: 'flex', gap: 8 }}>
                <input value={newSupplier} onChange={e => setNewSupplier(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addSupplier())}
                  placeholder="Νέος προμηθευτής..." style={{ flex: 1 }} />
                <button className="btn-primary" onClick={addSupplier}>Προσθήκη</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function formatEur(v) { return `€${parseFloat(v || 0).toFixed(2)}`; }
function formatDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('el-GR', { day: '2-digit', month: '2-digit', year: 'numeric' });
}
function SearchIcon() {
  return <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/></svg>;
}
function CloseIcon() {
  return <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6 6 18M6 6l12 12"/></svg>;
}